import { useMemo } from "react";
import { useOutletContext } from "react-router-dom";

import { validateDecimalValue } from "@components/Amount";

import type { SetState, ImportError, CustomAmounts, OutletContext } from "../types";

const TIER_COLUMN_PREFIX = "Target";
const TARGET_INVESTMENT_ID_COLUMN = "Target Investment ID";
const SOURCE_INVESTMENT_ID_COLUMN = "Source Investment ID";

const CENTS_IN_DOLLAR = 100;

type Row = Record<string, string | undefined>;

// A parsed row keeps the raw text of each tier column until buildRow turns
// it into cents.
export type ParsedItem = {
  investmentId: string;
  sourceInvestmentId?: string;
} & Record<string, string | undefined>;

type Item = {
  id: string;
};

const normalizeAmountValue = (value: string): string => {
  const normalizedValue = value
    .replace("$", "")
    .replaceAll(",", "");

  return normalizedValue;
};

const useTemplateParser = (
  setErrors: SetState<ImportError[]>,
  getItems: () => Item[],
  targetProjectId?: string,
) => {
  const { getProject } = useOutletContext<OutletContext>();

  const targetProject = getProject(targetProjectId);

  const targetProjectTierIds = useMemo(() =>
    targetProject
      ? targetProject.tiers.map(({ name }) => name)
      : []
  , [ targetProject ]);

  const parseRow = (row: Row): ParsedItem => {
    const item: ParsedItem = {
      investmentId: row[TARGET_INVESTMENT_ID_COLUMN] || row[SOURCE_INVESTMENT_ID_COLUMN],
      sourceInvestmentId: row[SOURCE_INVESTMENT_ID_COLUMN],
    };

    for (const tierId of targetProjectTierIds) {
      const TIER_COLUMN = `${TIER_COLUMN_PREFIX} ${tierId}`;
      item[tierId] = row[TIER_COLUMN];
    }

    return item;
  };

  const validateRow = (item: ParsedItem, rowIndex: number): boolean => {
    const { investmentId } = item;

    const investments = getItems();

    const getInvestment = (id: string) => investments.find((investment) => investment.id === id);

    const isInvestmentFound = !!getInvestment(investmentId);

    if (!isInvestmentFound) {
      setErrors((prevItems = []) => [
        ...prevItems,
        {
          index: rowIndex + 1,
          reason: `Includes invalid investment ID: ${investmentId}`,
        },
      ]);

      return false;
    }

    for (const tierId of targetProjectTierIds) {
      const value = item[tierId];

      if (value) {
        const normalizedValue = normalizeAmountValue(value);
        const isValid = validateDecimalValue(normalizedValue);

        if (!isValid) {
          const TIER_COLUMN = `${TIER_COLUMN_PREFIX} ${tierId}`;

          setErrors((prevItems = []) => [
            ...prevItems,
            {
              index: rowIndex + 1,
              reason: `Column "${TIER_COLUMN}" value has invalid format: ${value}`,
            },
          ]);

          return false;
        }
      }
    }

    return true;
  };

  const buildRow = (parsedItem: ParsedItem): CustomAmounts => {
    const item: CustomAmounts = { ...parsedItem };

    for (const tierId of targetProjectTierIds) {
      const value = parsedItem[tierId];

      if (value) {
        const normalizedValue = normalizeAmountValue(value);
        const centsValue = Math.round(parseFloat(normalizedValue) * CENTS_IN_DOLLAR);

        item[tierId] = centsValue;
      }

      if (!value) {
        delete item[tierId];
      }
    }

    return item;
  };

  return {
    buildRow,
    parseRow,
    validateRow,
  };
};

export default useTemplateParser;
